import { useCallback, useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import {
  IconChartBarOff,
  IconChartPieOff,
  IconCheck,
} from '@tabler/icons-react';

import { elevations, Typography } from '@zendesk/forethought-elements';
import {
  getEventBasedAggregationTypeOptions,
  getFunnelAggregationTypeOptions,
} from '../cards/aggregationTypeOptions';
import {
  dateRangeToTimestamp,
  numbersToStringWithCommas,
  numberToCompactNotation,
} from '../helpers';
import { useChatsTableRequestParams } from '../hooks/useChatsTableRequestParams';
import { ChartTooltip } from './ChartTooltip';
import { formatHourlyXValue } from './helpers';
import MetricChart from './MetricChart';
import { GRAPH_CHART_COLORS } from 'src/constants/colors';
import { useStateParams } from 'src/hooks/hooks';
import { useGetEventConfigurationsQuery } from 'src/services/custom-events';
import { useFetchDatasetQuery } from 'src/services/insight-cards';
import {
  AggregationType,
  ChartType,
  CustomEventChartBase,
  DatasetResponse,
  MeasurementType,
  SeriesItem,
} from 'src/services/insight-cards/types';
import { ConversationChannel } from 'src/types/workflowBuilderAPITypes';
import {
  dateRangeDeserialize,
  dateRangeSerialize,
  timeFilterParameterValidator,
} from 'src/utils/discover/helpers';
import { last30DaysTimeRange } from 'src/utils/timeRangeHelpers';

// Highcharts' HTML sanitizer strips `viewBox` from inline SVGs by default,
// which breaks tabler icons rendered into chart titles via renderToString.
Highcharts.AST.allowedAttributes.push('viewBox');

const CURRENT_STEP_VALUE = 'Current Step Value';

export default function Chart({
  channels,
  chart,
  endTimestamp: endTimestampOverride,
  isChartNameVisible = true,
  isDetailView = false,
  isPrimary = false,
  shouldUseChatsTableRequestParams,
  showEmptyState = false,
  startTimestamp: startTimestampOverride,
  titleFontSize = '16px',
}: {
  channels: ConversationChannel[];
  chart: CustomEventChartBase;
  endTimestamp?: number;
  isChartNameVisible?: boolean;
  isDetailView?: boolean;
  isPrimary?: boolean;
  shouldUseChatsTableRequestParams: boolean;
  showEmptyState?: boolean;
  startTimestamp?: number;
  titleFontSize?: string;
}) {
  const theme = useTheme();
  const { t } = useTranslation('solve-insights');
  const [dateRange] = useStateParams({
    deserialize: dateRangeDeserialize,
    initialState: last30DaysTimeRange,
    paramsName: 'date',
    serialize: dateRangeSerialize,
    validator: timeFilterParameterValidator([]),
  });

  const {
    end_timestamp: defaultEndTimestamp,
    start_timestamp: defaultStartTimestamp,
  } = dateRangeToTimestamp(dateRange);
  const start_timestamp = startTimestampOverride ?? defaultStartTimestamp;
  const end_timestamp = endTimestampOverride ?? defaultEndTimestamp;

  const chatsTableRequestParams = useChatsTableRequestParams(channels);

  const { data: dataset } = useFetchDatasetQuery({
    aggregation_type: chart.aggregation_type,
    channels,
    ...(shouldUseChatsTableRequestParams ? chatsTableRequestParams : {}),
    ...(chart.chart_type === 'line' && {
      chart_type: chart.chart_type,
      period: chart.period,
    }),
    derived_metric: chart.derived_metric,
    end_timestamp,
    formula_text: chart.formula_text,
    group_by: chart.group_by,
    measurement_type: chart.measurement_type,
    properties: chart.properties,
    start_timestamp,
  });

  const { data: eventConfigurations = [] as const } =
    useGetEventConfigurationsQuery({
      draft_mode: 'any',
    });

  const highchartsContainerProps = useMemo(() => {
    return {
      style: {
        containerType: 'size',
        height: '100%',
        width: '100%',
      },
    };
  }, []);

  const transformChartData = useCallback(
    (
      chartType: ChartType,
      measurementType: MeasurementType,
      sourceData: DatasetResponse = {
        chart_type: 'bar',
        is_grouped: false,
        series: [],
        total_sum: null,
        x_values: [],
      } as const,
    ): Highcharts.Options => {
      const aggregationTypeLabel = (
        measurementType === 'funnel'
          ? getFunnelAggregationTypeOptions(t)
          : getEventBasedAggregationTypeOptions(t)
      ).find(option => option.value === chart.aggregation_type)?.label;
      const sourceDataWithCurrentChartType = {
        ...sourceData,
        chart_type: chartType === 'line' ? sourceData.chart_type : chartType,
      };

      if (sourceDataWithCurrentChartType.is_grouped) {
        switch (sourceDataWithCurrentChartType.chart_type) {
          case 'bar':
            return {
              chart: {
                events: {
                  render() {
                    // Center-align multiline SVG data labels.
                    // Highcharts SVG renderer left-aligns <br/> tspans,
                    // so we post-process to set text-anchor: middle and
                    // reposition x to the center of the label rect.
                    const chart = this as Highcharts.Chart;
                    chart.series.forEach(series => {
                      series.points.forEach(point => {
                        const dl = (
                          point as unknown as {
                            dataLabel?: Highcharts.SVGElement;
                          }
                        ).dataLabel;
                        if (!dl) {
                          return;
                        }
                        const rect = dl.element.querySelector('rect');
                        const text = dl.element.querySelector('text');
                        if (!rect || !text) {
                          return;
                        }
                        const rectWidth = parseFloat(
                          rect.getAttribute('width') || '0',
                        );
                        const centerX = String(rectWidth / 2);
                        text.setAttribute('text-anchor', 'middle');
                        text.setAttribute('x', centerX);
                        text
                          .querySelectorAll('tspan[x]')
                          .forEach((tspan: Element) =>
                            tspan.setAttribute('x', centerX),
                          );
                      });
                    });
                  },
                },
                type: 'column',
              },
              legend: {
                itemStyle: {
                  fontSize: '14px',
                },
                labelFormatter() {
                  const variantMetadata =
                    sourceDataWithCurrentChartType.variant_metadata?.find(
                      metadata => this.name === metadata.variant_name,
                    );

                  return variantMetadata?.is_leader
                    ? `🏆 ${this.name}`
                    : this.name;
                },
              },
              series: sourceDataWithCurrentChartType.x_values.map(
                (xValue, seriesIndex) => {
                  const variantMetadata =
                    sourceDataWithCurrentChartType.variant_metadata?.find(
                      metadata =>
                        sourceDataWithCurrentChartType.series[0][seriesIndex]
                          .label === metadata.variant_name,
                    );
                  const deltaFromControl =
                    variantMetadata?.delta_from_control || 0;

                  return {
                    data: [
                      {
                        ...(sourceDataWithCurrentChartType.variant_metadata
                          ? {
                              dataLabels: {
                                backgroundColor: theme.palette.colors.white,
                                borderColor: '#D7CECE',
                                borderRadius: 8,
                                borderWidth: 1,
                                crop: false,
                                enabled: true,
                                formatter() {
                                  const deltaColor =
                                    deltaFromControl === 0
                                      ? theme.palette.text.primary
                                      : deltaFromControl > 0
                                        ? theme.palette.colors.green[500]
                                        : theme.palette.colors.red[500];
                                  const deltaText = `${deltaFromControl > 0 ? '+' : ''}${numbersToStringWithCommas(
                                    {
                                      number: deltaFromControl / 100,
                                      style: 'percent',
                                    },
                                  )}`;
                                  const valueText = numbersToStringWithCommas({
                                    number:
                                      sourceDataWithCurrentChartType.series[0][
                                        seriesIndex
                                      ].data,
                                    style: 'decimal',
                                  });
                                  const valueSpan = `<span style="color: ${theme.palette.text.secondary}; font-weight: 600; font-size: 13px;">${valueText}</span>`;

                                  if (variantMetadata?.is_control) {
                                    return valueSpan;
                                  }
                                  return `<span style="color: ${deltaColor}; font-weight: 500; font-size: 14px;">${deltaText}</span><br/>${valueSpan}`;
                                },
                                overflow: 'allow',
                                padding: 8,
                                style: {
                                  fontFamily: theme.typography
                                    .fontFamily as string,
                                  textAlign: 'center',
                                },
                                useHTML: false,
                                y: -5,
                              } as const satisfies Highcharts.DataLabelsOptions,
                            }
                          : {}),
                        name: eventConfigurations.find(
                          configuration =>
                            configuration.event_configuration_id ===
                            chart.properties[0]?.property_id,
                        )?.name,
                        y: sourceDataWithCurrentChartType.series[0][seriesIndex]
                          .data,
                      },
                    ],
                    name: xValue,
                    type: 'column',
                  };
                },
              ),
              xAxis: { visible: false },
              yAxis: {
                labels: {
                  style: {
                    fontSize: '14px',
                  },
                },
                title: {
                  style: {
                    fontSize: '14px',
                  },
                  text: aggregationTypeLabel,
                },
                visible: true,
              },
            };
          case 'line': {
            // For grouped line/bar: each group is a series (e.g., each device type), each item is a value for each x_value (date)
            return {
              chart: { type: 'spline' },
              plotOptions: {
                spline: {
                  lineWidth: 1,
                  marker: {
                    enabled: false,
                    radius: 3,
                    states: {
                      hover: {
                        enabled: true,
                        radius: 4,
                      },
                    },
                    symbol: 'circle',
                  },
                  states: {
                    hover: {
                      lineWidth: 1,
                    },
                  },
                },
              },
              series: sourceDataWithCurrentChartType.series.map(
                (group, groupIdx) => ({
                  data: group.map(item => item.data),
                  lineWidth: 1,
                  name:
                    group[0]?.label ??
                    t('cards.groupFallback', { number: groupIdx + 1 }),
                  type: 'spline',
                }),
              ),
              xAxis: {
                categories:
                  sourceDataWithCurrentChartType.period === 'hourly'
                    ? sourceDataWithCurrentChartType.x_values.map(
                        formatHourlyXValue,
                      )
                    : sourceDataWithCurrentChartType.x_values,
                visible: true,
              },
              yAxis: {
                title: {
                  text: aggregationTypeLabel,
                },
                visible: true,
              },
            };
          }
          case 'pie': {
            const totalSum = sourceData?.total_sum || 0;
            const centerContent = renderToString(
              <PieChartCenterContent
                aggregationType={chart.aggregation_type}
                t={t}
                totalSum={totalSum}
              />,
            );

            return {
              chart: { type: 'pie' },
              plotOptions: {
                pie: {
                  dataLabels: {
                    enabled: true,
                    style: { fontSize: '16px' },
                  },
                  innerSize: '80%',
                  size: '90%',
                },
              },
              series: sourceDataWithCurrentChartType.series.map(s => ({
                data: s.map(item => ({
                  name: item.label,
                  y: item.data,
                })),
                name: aggregationTypeLabel,
                type: 'pie',
              })),
              subtitle: {
                align: 'center',
                text: centerContent,
                useHTML: false,
                verticalAlign: 'middle',
                y: 50,
              },
              xAxis: { visible: false },
              yAxis: { visible: false },
            };
          }
          case 'number':
            // Highcharts is not used for 'number' type charts, return empty
            // object to avoid errors:
            return {};

          default:
            throw new Error(
              `Unsupported chart type: ${sourceDataWithCurrentChartType.chart_type}`,
            );
        }
      }

      switch (sourceDataWithCurrentChartType.chart_type) {
        case 'bar':
          // For funnel measurement type, create overlaid series for funnel visualization
          if (measurementType === 'funnel') {
            const actualData = sourceDataWithCurrentChartType.series.map(s => ({
              dataLabels: {
                backgroundColor: theme.palette.colors.white,
                borderColor: '#D7CECE',
                borderRadius: 8,
                borderWidth: 1,
                crop: false,
                enabled: true,
                formatter() {
                  return renderToString(
                    <div
                      style={{
                        alignItems: 'center',
                        background: theme.palette.colors.white,
                        border: '1px solid #D7CECE',
                        borderRadius: '8px',
                        display: 'inline-flex',
                        flexDirection: 'column',
                        padding: '4px 12px',
                      }}
                    >
                      <div
                        style={{
                          color: theme.palette.text.primary,
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        {numbersToStringWithCommas({
                          number: s.percentage_baseline / 100,
                          style: 'percent',
                        })}
                      </div>
                      <br />
                      <div
                        style={{
                          color: theme.palette.text.secondary,
                          fontFamily: theme.typography.fontFamily,
                          fontSize: '11px',
                          fontWeight: '600',
                        }}
                      >
                        {numbersToStringWithCommas({
                          number: s.data,
                          style: 'decimal',
                        })}
                      </div>
                    </div>,
                  );
                },
                overflow: 'allow',
                // It used to be `useHTML: true`, but after package upgrades it
                // got broken (UI alignment issues). Also had to add top-level
                // styling props for background color and border to make it look
                // similar to the old UI.
                useHTML: false,
                y: 22,
              } as const satisfies Highcharts.DataLabelsOptions,
              y: s.data,
            }));

            // Create ghost data showing the previous step's value
            const ghostData = sourceDataWithCurrentChartType.series.map(
              (_, index) => {
                // For the first step, null (no ghost bar)
                if (index === 0) {
                  return null;
                }
                // For subsequent steps, show the previous step's value as ghost
                return (
                  sourceDataWithCurrentChartType.series[index - 1]?.data || 0
                );
              },
            );

            const categories = sourceDataWithCurrentChartType.series.map(
              s => s.label,
            );

            return {
              chart: { type: 'column' },
              legend: { enabled: false },
              plotOptions: {
                column: {
                  borderWidth: 0,
                  grouping: false,
                  pointPadding: 0.1,
                  shadow: false,
                },
              },
              series: [
                {
                  colorIndex: 0,
                  data: ghostData,
                  enableMouseTracking: false,
                  name: 'Previous Step Total',
                  opacity: 0.3,
                  showInLegend: false,
                  type: 'column',
                },
                {
                  colorIndex: 0,
                  data: actualData,
                  name: CURRENT_STEP_VALUE,
                  type: 'column',
                },
              ],
              xAxis: {
                categories,
                visible: true,
              },
              yAxis: {
                title: {
                  text: aggregationTypeLabel,
                },
                visible: true,
              },
            };
          }
          // Standard bar chart for non-funnel measurement types
          return {
            chart: { type: 'column' },
            series: sourceDataWithCurrentChartType.series.map(s => ({
              data: [s.data],
              name: s.label,
              type: 'column',
            })),
            xAxis: { categories: [''], visible: true },
            yAxis: {
              title: {
                text: aggregationTypeLabel,
              },
              visible: true,
            },
          };
        case 'pie': {
          const totalSum = sourceData?.total_sum || 0;
          const centerContent = renderToString(
            <PieChartCenterContent
              aggregationType={chart.aggregation_type}
              t={t}
              totalSum={totalSum}
            />,
          );

          return {
            chart: { type: 'pie' },
            plotOptions: {
              pie: {
                dataLabels: { enabled: true },
                innerSize: '80%',
                size: '90%',
              },
            },
            series: [
              {
                data: sourceDataWithCurrentChartType.series.map(s => ({
                  name: s.label,
                  y: s.data,
                })),
                name: aggregationTypeLabel,
                type: 'pie',
              },
            ],
            subtitle: {
              align: 'center',
              text: centerContent,
              useHTML: false,
              verticalAlign: 'middle',
              y: isDetailView ? 40 : 50,
            },
            xAxis: { visible: false },
            yAxis: { visible: false },
          };
        }
        case 'number':
          // Highcharts is not used for 'number' type charts, return empty
          // object to avoid errors:
          return {};

        default:
          throw new Error(
            `Unsupported chart type: ${sourceDataWithCurrentChartType.chart_type}`,
          );
      }
    },
    [
      chart.aggregation_type,
      chart.properties,
      eventConfigurations,
      isDetailView,
      t,
      theme.palette.colors.green,
      theme.palette.colors.red,
      theme.palette.colors.white,
      theme.palette.text.primary,
      theme.palette.text.secondary,
      theme.typography.fontFamily,
    ],
  );

  const highchartsOptions: Highcharts.Options = useMemo(() => {
    const transformedData = transformChartData(
      chart.chart_type,
      chart.measurement_type,
      dataset,
    );

    return {
      ...transformedData,
      chart: {
        ...transformedData.chart,
        backgroundColor: 'transparent',
      },
      colors: GRAPH_CHART_COLORS,
      credits: {
        enabled: false,
      },
      // Explicitly handle subtitle otherwise it will persist when changing from pie to
      subtitle: transformedData.subtitle
        ? transformedData.subtitle
        : { text: '' },
      title: {
        align: 'left',
        style: {
          color: theme.palette.text.primary,
          fontFamily: theme.typography.fontFamily,
          fontSize: titleFontSize,
          fontWeight: '600',
          paddingBottom: chart.chart_type === 'bar' ? '24px' : 0,
        },
        text: isChartNameVisible
          ? dataset?.variant_metadata && chart.chart_type === 'bar'
            ? renderToString(
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  {chart.name}
                  {dataset?.variant_metadata?.some(
                    variant => variant.is_statistically_significant,
                  ) && (
                    <div
                      style={{
                        backgroundImage:
                          'linear-gradient(135deg, #FFB393 0%, #ABD5FA 50%, #12A6B4 100%)',
                        backgroundOrigin: 'border-box',
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        boxShadow: 'inset 0 100vw white',
                        flexShrink: 0,
                        height: '22px',
                      }}
                    >
                      <div
                        style={{
                          alignItems: 'center',
                          background:
                            'linear-gradient(135deg, rgba(255, 179, 147, 0.15) 0%, rgba(171, 213, 250, 0.15) 50%, rgba(18, 166, 180, 0.15) 100%)',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          justifyContent: 'space-evenly',
                          padding: '0 8px',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            alignItems: 'center',
                            color: theme.palette.colors.teal[500],
                            display: 'flex',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '11px',
                            fontWeight: '600',
                            gap: '2px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <IconCheck size={12} />
                          {t('cards.statisticallySignificant')}
                        </div>
                      </div>
                    </div>
                  )}
                  {isPrimary && (
                    <div
                      style={{
                        alignItems: 'center',
                        backgroundColor: theme.palette.colors.slate[100],
                        borderRadius: '4px',
                        color: theme.palette.colors.grey[700],
                        display: 'flex',
                        flexShrink: 0,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '11px',
                        fontWeight: '600',
                        height: '22px',
                        marginLeft: 'auto',
                        padding: '0 8px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('cards.view.chartPrimaryBadge')}
                    </div>
                  )}
                </div>,
              )
            : chart.name
          : '',
        useHTML: true,
      },
      tooltip: {
        backgroundColor: theme.palette.colors.white,
        borderColor: theme.palette.colors.slate[200],
        borderRadius: 4,
        ...(chart.measurement_type === 'funnel' ? { distance: 30 } : {}),
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const currentPointIndex = this.point?.index;
          const seriesIndex = this.series?.index || 0;

          const getTooltipData = (): {
            aggregationLabel: string | null;
            eventName: string | null;
            groupByLabel: string | null;
            percentage: number | null;
            percentage_baseline: number | null;
            source_items: SeriesItem[] | null;
          } => {
            if (!dataset || currentPointIndex === undefined) {
              return {
                aggregationLabel: null,
                eventName: null,
                groupByLabel: null,
                percentage: null,
                percentage_baseline: null,
                source_items: null,
              };
            }

            // Special handling for funnel charts
            if (chart.measurement_type === 'funnel') {
              // For funnel charts, we need to find the correct data item from the original dataset
              // based on the point index (category position)
              const dataItem = dataset.is_grouped
                ? dataset.series[0]?.[currentPointIndex]
                : dataset.series[currentPointIndex];

              return {
                aggregationLabel: dataItem?.aggregation_label ?? null,
                eventName: dataItem?.event_name ?? null,
                groupByLabel: dataItem?.group_by_label ?? null,
                percentage: dataItem?.percentage ?? null,
                percentage_baseline: dataItem?.percentage_baseline ?? null,
                source_items: dataItem?.source_items ?? null,
              };
            }

            if (dataset.is_grouped) {
              // For grouped data, the indexing depends on chart type
              if (chart.chart_type === 'line') {
                // For line charts: seriesIndex is the line, currentPointIndex is the x-axis position
                const seriesData = dataset.series[seriesIndex];
                const seriesItem = seriesData?.[currentPointIndex];

                return {
                  aggregationLabel: seriesItem?.aggregation_label ?? null,
                  eventName: seriesItem?.event_name ?? null,
                  groupByLabel: seriesItem?.group_by_label ?? null,
                  percentage: seriesItem?.percentage ?? null,
                  percentage_baseline: seriesItem?.percentage_baseline ?? null,
                  source_items: seriesItem?.source_items ?? null,
                };
              } else {
                // For bar/pie charts: use existing logic
                const firstGroup = dataset.series[0];
                const itemIndex =
                  chart.chart_type === 'bar' ? seriesIndex : currentPointIndex;
                const seriesItem = firstGroup[itemIndex];
                const variantMetadata = dataset.variant_metadata?.find(
                  metadata => seriesItem.label === metadata.variant_name,
                );

                return {
                  aggregationLabel: seriesItem?.aggregation_label ?? null,
                  eventName: seriesItem?.event_name ?? null,
                  groupByLabel: seriesItem?.group_by_label ?? null,
                  percentage: seriesItem?.percentage ?? null,
                  percentage_baseline:
                    seriesItem?.percentage_baseline ??
                    variantMetadata?.delta_from_control ??
                    null,
                  source_items: seriesItem?.source_items ?? null,
                };
              }
            } else {
              // For non-grouped data, get data from the series at the current index
              const flatSeries = dataset.series;

              // For bar charts: seriesIndex represents which bar
              // For pie charts: currentPointIndex represents which slice
              const itemIndex =
                chart.chart_type === 'bar' ? seriesIndex : currentPointIndex;
              const seriesItem = flatSeries[itemIndex];

              return {
                aggregationLabel: seriesItem?.aggregation_label ?? null,
                eventName: seriesItem?.event_name ?? null,
                groupByLabel: seriesItem?.group_by_label ?? null,
                percentage: seriesItem?.percentage ?? null,
                percentage_baseline: seriesItem?.percentage_baseline ?? null,
                source_items: seriesItem?.source_items ?? null,
              };
            }
          };

          const {
            aggregationLabel,
            eventName,
            percentage,
            percentage_baseline,
            source_items,
          } = getTooltipData();

          const getLabel = (): string => {
            if (
              chart.chart_type === 'bar' &&
              this.series.name !== CURRENT_STEP_VALUE
            ) {
              return this.series.name || this.key || '';
            }

            // For line charts, try to get the label from the data first
            if (chart.chart_type === 'line') {
              if (dataset?.is_grouped) {
                // For grouped line charts, use seriesIndex to get the correct group's label
                const groupData = dataset.series[seriesIndex];
                if (groupData && groupData[0]?.label) {
                  return groupData[0].label;
                }
              } else if (
                !dataset?.is_grouped &&
                dataset?.series[currentPointIndex]?.label
              ) {
                return dataset.series[currentPointIndex].label;
              }
            }

            return this.key || '';
          };

          const label = getLabel();

          return renderToString(
            <ChartTooltip
              aggregationLabel={aggregationLabel || undefined}
              aggregationType={chart.aggregation_type}
              chartType={chart.chart_type}
              color={typeof this.color === 'string' ? this.color : undefined}
              currentPointIndex={currentPointIndex}
              endTimestamp={end_timestamp}
              eventName={eventName}
              funnelConversationsCurrent={
                chart.measurement_type === 'funnel' ? this.y : undefined
              }
              funnelConversationsTotal={
                chart.measurement_type === 'funnel'
                  ? dataset?.total_sum
                  : undefined
              }
              isGrouped={dataset?.is_grouped}
              label={label}
              percentage={percentage}
              percentageBaseline={percentage_baseline}
              sourceItems={source_items}
              startTimestamp={start_timestamp}
              t={t}
              theme={theme}
              totalSum={dataset?.total_sum}
              value={this.y || 0}
              valuePrefix={dataset?.tooltip?.value_prefix}
              valueSuffix={dataset?.tooltip?.value_suffix}
            />,
          );
        },
        outside: true,
        style: {
          boxShadow: elevations.z3,
        },
        useHTML: true,
      },
    };
  }, [
    chart.aggregation_type,
    chart.chart_type,
    chart.measurement_type,
    chart.name,
    dataset,
    end_timestamp,
    isChartNameVisible,
    isPrimary,
    start_timestamp,
    t,
    theme,
    titleFontSize,
    transformChartData,
  ]);

  const isDataEmpty =
    showEmptyState &&
    dataset !== undefined &&
    (dataset.series.length === 0 ||
      (dataset.is_grouped &&
        dataset.series.every(group => group.length === 0)));

  const renderEmptyIcon = () => {
    switch (chart.chart_type) {
      case 'pie':
        return (
          <IconChartPieOff
            color={theme.palette.colors.grey[200]}
            size={80}
            stroke={1}
          />
        );
      case 'bar':
        return (
          <IconChartBarOff
            color={theme.palette.colors.grey[200]}
            size={80}
            stroke={1}
          />
        );
      default:
        return (
          <Box paddingBottom={1}>
            <Typography color={theme.palette.colors.grey[200]} variant='font32'>
              {t('cards.na')}
            </Typography>
          </Box>
        );
    }
  };

  if (isDataEmpty) {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          padding: '6px',
        }}
      >
        {isChartNameVisible && (
          <Typography variant='font16Bold'>{chart.name}</Typography>
        )}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            marginTop: '16px',
            rowGap: 1,
          }}
        >
          <Box alignItems='flex-end' display='flex' height={80}>
            {renderEmptyIcon()}
          </Box>
          <Typography color={theme.palette.colors.black} variant='font14'>
            {t('cards.emptyState.insufficientData')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (chart.chart_type === 'number') {
    return (
      <MetricChart
        chart={chart}
        dataset={dataset}
        endTimestamp={end_timestamp}
        isChartNameVisible={isChartNameVisible}
        startTimestamp={start_timestamp}
        titleFontSize={titleFontSize}
      />
    );
  }

  return (
    <HighchartsReact
      containerProps={highchartsContainerProps}
      highcharts={Highcharts}
      options={highchartsOptions}
    />
  );
}

interface PieChartCenterContentProps {
  aggregationType: AggregationType;
  t: TFunction<'solve-insights'>;
  totalSum: number;
}

function PieChartCenterContent({
  aggregationType,
  t,
  totalSum,
}: PieChartCenterContentProps) {
  const getLabel = () => {
    switch (aggregationType) {
      case 'total':
        return t('cards.pieCenter.event', { count: totalSum });
      case 'unique':
        return t('cards.pieCenter.conversation', { count: totalSum });
      case 'avg':
        return t('cards.pieCenter.average');
      default:
        return aggregationType;
    }
  };

  const label = getLabel();

  return (
    <div
      style={{
        marginLeft: 'auto',
        marginRight: 'auto',
        position: 'relative',
        textAlign: 'center',
        width: '28cqw',
        zIndex: -1,
      }}
    >
      <div
        style={{
          color: 'black',
          fontSize: 'clamp(1rem, 10cqh, 10rem)',
          fontWeight: '600',
          marginBottom: label ? '12px' : '0',
          overflow: 'hidden',
          pointerEvents: 'none',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {numberToCompactNotation(totalSum)}
        <br />
        <br />
      </div>
      {label && (
        <div
          style={{
            color: 'black',
            fontSize: 'clamp(0.8rem, 8cqh, 24px)',
            overflow: 'hidden',
            pointerEvents: 'none',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}
